import getTableColumns, { getDefaultSettings } from './helpers/tableColumns';
import useFlexibleColumnsTable from '../../../hooks/useFlexibleColumnsTable';
import { LOCAL_STORAGE_KEYS } from '../../../shared/settingsNames';
import {
  useDeleteSubscriptionPlan,
  useGetPlans,
} from '../../../services/redux/api/subscriptionsApi';
import { getCellReducers } from './helpers/getCellReducers';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { ISubscriptionPlan } from '../../../types/interfaces/subscription-plan';
import { useAccess } from '../../../hooks/useAccess';
import { FormikProps, useFormik } from 'formik';
import {
  SubscriptionPeriod,
  SubscriptionScope,
} from '../../../shared/constants';

export function useSubscriptionPlansComponent(classes) {
  const { t } = useTranslation();
  const { data: plans } = useGetPlans();
  const [itemForDelete, setItemForDelete] = useState<ISubscriptionPlan>(null);
  const [itemForEditing, setItemForEditing] = useState<ISubscriptionPlan>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');
  const { access } = useAccess();

  const [deleteSubscriptionPlan] = useDeleteSubscriptionPlan();
  const { order: defaultOrder, orderBy } = getDefaultSettings(t);
  const allOption = { id: 'all', name: t('common.all') };
  const filterFormik: FormikProps<any> = useFormik({
    enableReinitialize: true,
    initialValues: {
      scope: allOption,
      period: allOption,
    },
    onSubmit: () => {},
  });
  const filteredValues = useMemo(
    () =>
      plans?.filter((plan: ISubscriptionPlan) => {
        const scopeFilterId = filterFormik.values.scope?.id ?? 'all';
        const periodFilterId = filterFormik.values.period?.id ?? 'all';
        const filterValue = filter.toLowerCase();

        if (
          !String(plan.stripePlanId ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(plan.scope ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !String(plan.period ?? '')
            .toLowerCase()
            .includes(filterValue) &&
          !plan.price?.toString().includes(filterValue)
        ) {
          return false;
        }

        if (scopeFilterId !== 'all' && plan.scope !== scopeFilterId)
          return false;

        if (periodFilterId !== 'all' && plan.period !== periodFilterId)
          return false;

        return true;
      }),
    [plans, filter, filterFormik.values],
  );

  const cellReducers = getCellReducers(
    t,
    classes,
    {
      canEdit: access?.subscriptionPlans?.canEdit ?? false,
      canDelete: access?.subscriptionPlans?.canDelete ?? false,
    },
    (plan: ISubscriptionPlan) => {
      setItemForEditing(plan);
      setShowEditModal(true);
    },
    (plan: ISubscriptionPlan) => {
      setItemForDelete(plan);
    },
  );

  const useTableData = useFlexibleColumnsTable(
    filteredValues,
    LOCAL_STORAGE_KEYS.SUBSCRIPTION_PLAN_TABLE_SETTINGS,
    getTableColumns(t),
    orderBy,
    defaultOrder,
  );

  const {
    order,
    columnsListForPopup,
    columnsList,
    onToggleHideColumn,
    onRestoreDefault,
    sortedArray,
    onSortColumns,
    onSort,
  } = useTableData;

  function onItemDelete() {
    setItemForDelete(null);
    deleteSubscriptionPlan({ id: itemForDelete.id });
  }

  const filterScopeOptions = [
    allOption,
    ...Object.values(SubscriptionScope).map((scope) => ({
      id: scope,
      name: scope,
    })),
  ];

  const filterPeriodOptions = [
    allOption,
    ...Object.values(SubscriptionPeriod).map((period) => ({
      id: period,
      name: period,
    })),
  ];

  return {
    sortedArray,
    columnsList,
    order,
    orderBy,
    onSort,
    onSortColumns,
    cellReducers,
    itemForDelete,
    setItemForDelete,
    onItemDelete,
    filter,
    setFilter,
    showEditModal,
    setShowEditModal,
    itemForEditing,
    setItemForEditing,
    filterFormik,
    filterScopeOptions,
    filterPeriodOptions,
  };
}
